import { useEffect, useRef } from "react";
import useCartStore from "../../stores/useCartStore";
import db from "../../database";
import FeedbackWaitingModal from "../PointOfSale/FeedbackWaitingModal";

/**
 * CustomerDisplayBridge — Plugin bridge component
 *
 * Bridges POS cart → Customer Display plugin via IPC.
 * Only opens the display when:
 *   1. The customer-display plugin is enabled in plugin_registry
 *   2. The user explicitly requests it (via "Open Display" button / cd:openDisplay event)
 * Respects user close — does NOT auto-reopen after user closes the window.
 *
 * Modes: idle → cart → processing (payment) → feedback → idle
 */
const CustomerDisplayBridge = () => {
  const feedbackInProgressRef = useRef(false); // blocks cart updates during processing/feedback
  const displayReadyRef = useRef(false);
  const userClosedRef = useRef(false);
  const sendingRef = useRef(false); // prevents concurrent IPC sends
  const debounceTimerRef = useRef(null);

  const getApi = () => window.electronAPI?.plugin?.custDisplay || null;

  const isPluginEnabled = async () => {
    const api = getApi();
    if (!api?.isEnabled) return false;
    try {
      return await api.isEnabled();
    } catch {
      return false;
    }
  };

  // Check display status on mount — recovers state after component remount
  useEffect(() => {
    const checkDisplayStatus = async () => {
      const api = getApi();
      if (!api) return;

      const enabled = await isPluginEnabled();
      if (!enabled) return;

      try {
        const status = await api.getStatus();
        if (status?.windowOpen) {
          displayReadyRef.current = true;
          userClosedRef.current = false;
        }
      } catch {}
    };

    checkDisplayStatus();
  }, []);

  const transformCart = (cartObj) => {
    if (!cartObj) return null;
    const items = cartObj.items || [];
    return {
      items: items.map((item) => ({
        id: item.productId || item.lineId || item.key,
        name: item.name || item.productName || "Item",
        qty: Math.abs(item.weight || 1),
        price: Math.abs(item.sunitprice || item.realPrice || 0),
        nettotal: Math.abs(item.nettotal || 0),
        isReturn: item.isReturn || false,
      })),
      subtotal: Math.abs(cartObj.subTotal || (cartObj.total || 0) - (cartObj.tax || 0)),
      tax: Math.abs(cartObj.tax || 0),
      discount: Math.abs(cartObj.discount || 0),
      total: Math.abs(cartObj.total || 0),
      currency: "₹",
      sOrderID: cartObj.sOrderID,
      documentno: cartObj.documentno,
    };
  };

  const sendCartToDisplay = async (cartObj) => {
    if (feedbackInProgressRef.current) return;
    if (userClosedRef.current) return;
    if (!displayReadyRef.current) return;
    if (sendingRef.current) return; // skip if previous send still in-flight

    const api = getApi();
    if (!api) return;

    sendingRef.current = true;

    const displayCart = transformCart(cartObj);
    if (!displayCart) {
      sendingRef.current = false;
      return;
    }

    try {
      if (displayCart.items.length > 0) {
        await api.updateCart(displayCart);
      } else {
        await api.setMode("idle");
      }
    } catch {
      displayReadyRef.current = false;
    } finally {
      sendingRef.current = false;
    }
  };

  // Debounced wrapper — batches rapid cart changes into a single IPC send
  const debouncedSendCart = (cartObj) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      sendCartToDisplay(cartObj);
    }, 150);
  };

  // Listen for explicit open/close events (from UI buttons / pluginStore)
  useEffect(() => {
    const handleOpenDisplay = async () => {
      userClosedRef.current = false;
      await new Promise((r) => setTimeout(r, 1500));
      displayReadyRef.current = true;
      const cart = useCartStore.getState().cart;
      if (cart?.items?.length > 0) {
        sendCartToDisplay(cart);
      }
    };

    const handleCloseDisplay = () => {
      userClosedRef.current = true;
      displayReadyRef.current = false;
      feedbackInProgressRef.current = false;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };

    window.addEventListener("cd:openDisplay", handleOpenDisplay);
    window.addEventListener("cd:closeDisplay", handleCloseDisplay);
    return () => {
      window.removeEventListener("cd:openDisplay", handleOpenDisplay);
      window.removeEventListener("cd:closeDisplay", handleCloseDisplay);
    };
  }, []);

  // Subscribe to cart changes — debounced to avoid IPC flood
  useEffect(() => {
    const unsubscribe = useCartStore.subscribe(
      (state) => state.cart,
      (cart) => {
        debouncedSendCart(cart);
      }
    );

    return () => {
      unsubscribe();
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Processing mode — show loading on customer display during payment
  useEffect(() => {
    const handleStartProcessing = async () => {
      if (userClosedRef.current) return;
      if (!displayReadyRef.current) return;

      // Block cart updates immediately (before any async call)
      feedbackInProgressRef.current = true;

      const api = getApi();
      if (!api) {
        console.warn("[CDBridge] No API available for processing mode");
        return;
      }

      try {
        await api.setMode("processing");
      } catch (err) {
        console.warn("[CDBridge] setMode('processing') failed:", err);
        // Don't reset feedbackInProgressRef — feedback will follow
      }
    };

    window.addEventListener("cd:startProcessing", handleStartProcessing);
    return () => window.removeEventListener("cd:startProcessing", handleStartProcessing);
  }, []);

  // Feedback trigger from payment flow
  useEffect(() => {
    const handleFeedbackTrigger = async (e) => {
      if (userClosedRef.current) return;
      if (!displayReadyRef.current) return;

      // Block cart updates immediately — BEFORE any async call
      feedbackInProgressRef.current = true;

      const api = getApi();
      if (!api) {
        console.warn("[CDBridge] No API available for feedback");
        feedbackInProgressRef.current = false;
        return;
      }

      const orderId = e.detail?.orderId;

      try {
        const config = await api.getConfig();
        const purpose = config?.displayPurpose || "billing_feedback";
        const enabled = config?.feedbackEnabled !== "false";

        if (purpose !== "billing_feedback" || !enabled) {
          feedbackInProgressRef.current = false;
          return;
        }

        await api.setMode("feedback");

        const timeout = parseInt(config?.feedbackTimeout || "60", 10);
        window.dispatchEvent(
          new CustomEvent("cd:feedbackStart", { detail: { timeout, orderId } })
        );
      } catch (err) {
        console.error("[CDBridge] Feedback trigger error:", err);
        feedbackInProgressRef.current = false;
      }
    };

    const handleFeedbackDone = () => {
      feedbackInProgressRef.current = false;
    };

    window.addEventListener("cd:triggerFeedback", handleFeedbackTrigger);
    window.addEventListener("cd:feedbackDone", handleFeedbackDone);
    return () => {
      window.removeEventListener("cd:triggerFeedback", handleFeedbackTrigger);
      window.removeEventListener("cd:feedbackDone", handleFeedbackDone);
    };
  }, []);

  // When customer submits feedback, attach it to the order for cloud sync
  useEffect(() => {
    const api = getApi();
    if (!api?.onFeedbackSubmitted) return;

    const unsub = api.onFeedbackSubmitted(async (feedbackData) => {
      const orderId = feedbackData?.orderId;
      if (!orderId) return;

      try {
        await db.orders.where("documentno").equals(orderId).modify((order) => {
          order.feedbackData = {
            rating: feedbackData.rating,
            comment: feedbackData.comment || null,
            categories: feedbackData.categories || null,
            submittedAt: Date.now(),
          };
          // Mark for re-sync so feedback gets included in cloud metaData
          order.isSynced = 0;
        });
      } catch (err) {
        console.warn("[CDBridge] Failed to update order with feedback:", err);
      }
    });

    return () => unsub();
  }, []);

  return <FeedbackWaitingModal />;
};

export default CustomerDisplayBridge;
